import type { User } from "firebase/auth";
import {
  useCallback, useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { appConfig } from "../config/appConfig";
import { appSessionService } from "../services/appSessionService";
import { authService } from "../services/authService";
import { functionsService } from "../services/functionsService";
import type { PurchasesSession } from "../services/purchasesService";
import {
  claimIncludesAdminRole
} from "../services/testRaceAccessService";
import { type AppBootstrapContextValue } from "./appBootstrapContext";
import { createDefaultPurchasesSession, normalizeClaims } from './appBootstrapPresentation';

const defaultServices = {
  get appSessionService() { return appSessionService; },
  get authService() { return authService; },
  get functionsService() { return functionsService; }
};

export function useAppBootstrapViewModel(services = defaultServices) {
  const { authService, functionsService, appSessionService } = services;

  const [currentUser, setCurrentUser] = useState<User | null>(
    authService.getCurrentUser()
  );
  const [authChecked, setAuthChecked] = useState<boolean>(false);
  const [claimsChecked, setClaimsChecked] = useState<boolean>(false);
  const [claims, setClaims] = useState<Record<string, unknown>>({});
  const [canAccessTestRaces, setCanAccessTestRaces] = useState<boolean>(false);
  const [raceAccessGeneration, setRaceAccessGeneration] = useState(0);
  const authSessionGenerationRef = useRef(0);
  const claimsSyncGenerationRef = useRef(0);
  const [purchases, setPurchases] = useState<PurchasesSession>(
    createDefaultPurchasesSession()
  );

  useEffect(() => {
    let isMounted = true;
    let receivedAuthState = false;
    const initialUser = authService.getCurrentUser();

    const syncClaimsAndPurchases = async (user: User | null, generation: number, claimsGeneration: number) => {
      const isCurrentSync = () => isMounted &&
        authSessionGenerationRef.current === generation &&
        authService.getCurrentUser() === user;
      const isCurrentClaims = () => isCurrentSync() &&
        claimsSyncGenerationRef.current === claimsGeneration;
      try {
        if (user) {
          const tokenResult = await appSessionService.getClaims(user);
          if (!isCurrentSync()) return;
          const nextClaims = normalizeClaims(tokenResult);
          const nextIsAdmin = claimIncludesAdminRole(nextClaims);
          if (isCurrentClaims()) {
            setClaims(nextClaims);
            setClaimsChecked(true);
            setCanAccessTestRaces(nextIsAdmin);
          }

          if (!nextIsAdmin && isCurrentClaims()) {
            try {
              const accessState = await appSessionService.getRaceAccess();
              if (isCurrentClaims()) {
                setCanAccessTestRaces(
                  Boolean(accessState.canAccessTestRaces || accessState.isAdmin)
                );
              }
            } catch {
              if (isCurrentClaims()) {
                setCanAccessTestRaces(false);
              }
            }
          }
        } else if (isCurrentSync()) {
          setClaims({});
          setClaimsChecked(true);
          setCanAccessTestRaces(false);
        }

        // Subscription checks are account functionality. Do not create an
        // anonymous RevenueCat visitor or contact RevenueCat before sign-in.
        if (!user) {
          if (isCurrentSync()) {
            setPurchases(createDefaultPurchasesSession());
          }
          return;
        }

        if (!appConfig.purchases.publicApiKey.trim()) {
          if (isCurrentSync()) {
            setPurchases(
              createDefaultPurchasesSession({
                apiKeyPresent: false,
                error: "Missing RevenueCat public API key.",
              })
            );
          }
          return;
        }

        const purchasesService = await appSessionService.loadPurchasesService();
        if (!isCurrentSync()) return;
        const purchasesSession = await purchasesService.configure(
          appConfig.purchases.publicApiKey,
          user.uid
        );

        if (isCurrentSync()) {
          setPurchases(purchasesSession);
        }
      } catch {
        if (isCurrentClaims()) {
          setClaimsChecked(true);
          setCanAccessTestRaces(false);
        }
      }
    };

    const unsubscribe = authService.onAuthStateChanged((user) => {
      const generation = ++authSessionGenerationRef.current;
      const claimsGeneration = ++claimsSyncGenerationRef.current;
      if (receivedAuthState || user !== initialUser) {
        appSessionService.invalidateRaceAccess();
        setRaceAccessGeneration((current) => current + 1);
      }
      receivedAuthState = true;
      setCurrentUser(user);
      setAuthChecked(true);
      setClaimsChecked(false);
      setClaims({});
      setCanAccessTestRaces(false);
      setPurchases(createDefaultPurchasesSession());
      void syncClaimsAndPurchases(user, generation, claimsGeneration);
    });

    return () => {
      isMounted = false;
      authSessionGenerationRef.current += 1;
      claimsSyncGenerationRef.current += 1;
      appSessionService.invalidateRaceAccess();
      unsubscribe();
    };
  }, [appSessionService, authService]);

  useEffect(() => {
    if (!currentUser) return;
    const synchronize = () => {
      appSessionService.raceClock.reanchor();
      void appSessionService.raceClock.synchronize(() => functionsService.getRaceClockSample());
    };
    synchronize();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") synchronize();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", synchronize);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", synchronize);
    };
  }, [appSessionService.raceClock, currentUser, functionsService]);

  const refreshClaims = useCallback(async (forceRefresh = true) => {
    const generation = ++claimsSyncGenerationRef.current;
    appSessionService.invalidateRaceAccess();
    const isCurrentSync = () => claimsSyncGenerationRef.current === generation &&
      authService.getCurrentUser() === currentUser;
    if (!currentUser) {
      setClaims({});
      setClaimsChecked(true);
      setCanAccessTestRaces(false);
      setRaceAccessGeneration((current) => current + 1);
      return;
    }

    setClaimsChecked(false);
    try {
      const tokenResult = await appSessionService.getClaims(currentUser, forceRefresh);
      if (!isCurrentSync()) return;
      const nextClaims = normalizeClaims(tokenResult);
      const nextIsAdmin = claimIncludesAdminRole(nextClaims);
      setClaims(nextClaims);
      setCanAccessTestRaces(nextIsAdmin);
      if (!nextIsAdmin) {
        const accessState = await appSessionService.getRaceAccess({ throwOnFailure: true });
        if (!isCurrentSync()) return;
        setCanAccessTestRaces(
          Boolean(accessState.canAccessTestRaces || accessState.isAdmin)
        );
      }
    } catch (error) {
      if (isCurrentSync()) {
        setCanAccessTestRaces(false);
      }
      throw error;
    } finally {
      if (isCurrentSync()) {
        setClaimsChecked(true);
        setRaceAccessGeneration((current) => current + 1);
      }
    }
  }, [appSessionService, authService, currentUser]);

  const contextValue = useMemo<AppBootstrapContextValue>(
    () => ({
      currentUser,
      authChecked,
      claimsChecked,
      claims,
      isAuthenticated: Boolean(currentUser),
      isAdmin: claimIncludesAdminRole(claims),
      canAccessTestRaces,
      raceAccessGeneration,
      purchases,
      signOut: async () => {
        await authService.signOut();
        const generation = authSessionGenerationRef.current;
        const isStillSignedOut = () => !authService.getCurrentUser() &&
          authSessionGenerationRef.current === generation;
        if (!isStillSignedOut()) return;
        const purchasesService = await appSessionService.loadPurchasesService();
        if (!isStillSignedOut()) return;
        const purchasesSession = await purchasesService.logOut();
        if (isStillSignedOut()) setPurchases(purchasesSession);
      },
      refreshClaims,
    }),
    [currentUser, authChecked, claimsChecked, claims, canAccessTestRaces, raceAccessGeneration, purchases, refreshClaims, authService, appSessionService]
  );
  return { contextValue };
}
